import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Building2, Users, Activity, Shield, AlertTriangle, 
  TrendingUp, Database, Settings, LogOut, Menu, X,
  ToggleLeft, FileText, BarChart3, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const PLATFORM_NAV = [
  { name: 'Dashboard', icon: BarChart3, path: '/platform-admin' },
  { name: 'Hospitals', icon: Building2, path: '/platform-admin/hospitals' },
  { name: 'Feature Flags', icon: ToggleLeft, path: '/platform-admin/features' },
  { name: 'System Health', icon: Activity, path: '/platform-admin/health' },
  { name: 'Audit Logs', icon: FileText, path: '/platform-admin/audit-logs' },
  { name: 'Settings', icon: Settings, path: '/platform-admin/settings' },
];

export default function PlatformLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center py-6 px-4 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Platform Admin</h1>
                <p className="text-xs text-slate-400">Super Admin Console</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto">
            {PLATFORM_NAV.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path !== '/platform-admin' && location.pathname.startsWith(item.path));
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  data-testid={`platform-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-500/30'
                      : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="border-t border-slate-700 p-4">
            <div className="mb-3">
              <p className="text-sm font-medium text-white">{user?.full_name || 'Platform Admin'}</p>
              <p className="text-xs text-emerald-400 flex items-center gap-1">
                <Shield className="h-3 w-3" />
                Platform Owner
              </p>
            </div>
            <Button
              onClick={onLogout}
              data-testid="platform-logout-btn"
              className="w-full bg-slate-700 hover:bg-slate-600 text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-700 bg-slate-800/80 backdrop-blur-md px-4 md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden text-white hover:bg-slate-700"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">
              {PLATFORM_NAV.find(n => location.pathname.startsWith(n.path))?.name || 'Platform Admin'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-sm text-slate-400">System Online</span>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
